// utils/getPerpendicularIntersection.js
import * as THREE from 'three';

export function getPerpendicularIntersection(mousePoint, targetLine) {
  const a = targetLine.start;
  const b = targetLine.end;
  const ap = new THREE.Vector3().subVectors(mousePoint, a);
  const ab = new THREE.Vector3().subVectors(b, a).normalize();
  const projLength = ap.dot(ab);
  const projPoint = new THREE.Vector3().copy(ab).multiplyScalar(projLength).add(a);

  return projPoint;
}

/**
 * Bir çizginin X veya Y eksenine paralel olup olmadığını kontrol eder
 * @param {THREE.Vector3} start - Çizgi başlangıç noktası
 * @param {THREE.Vector3} end - Çizgi bitiş noktası
 * @param {number} tolerance - Açı toleransı (derece, varsayılan 1)
 * @returns {boolean} - true ise çizgi X veya Y eksenine paralel
 */
export function isAxisAligned(start, end, tolerance = 1) {
  const dir = new THREE.Vector3().subVectors(end, start);
  const angle = Math.atan2(Math.abs(dir.y), Math.abs(dir.x)) * (180 / Math.PI);
  // 0 veya 90 dereceye yakınsa eksen hizalı
  return angle < tolerance || angle > (90 - tolerance);
}

/**
 * Bir çizginin yönünü (direction) döndürür
 * @param {THREE.Vector3} start - Çizgi başlangıç noktası
 * @param {THREE.Vector3} end - Çizgi bitiş noktası
 * @returns {THREE.Vector3} - Normalize edilmiş yön vektörü
 */
export function getLineDirection(start, end) {
  return new THREE.Vector3().subVectors(end, start).normalize();
}

/**
 * İki çizgi (ray) arasındaki kesişim noktasını hesaplar
 * @param {THREE.Vector3} p1 - İlk ışın başlangıç noktası
 * @param {THREE.Vector3} d1 - İlk ışın yönü (normalize edilmiş)
 * @param {THREE.Vector3} p2 - İkinci ışın başlangıç noktası (veya çizgi start)
 * @param {THREE.Vector3} d2 - İkinci ışın yönü (veya çizgi direction, normalize edilmiş)
 * @returns {THREE.Vector3|null} - Kesişim noktası veya null
 */
export function getRayIntersection(p1, d1, p2, d2) {
  // 2D ray intersection (z = 0)
  // p1 + t1 * d1 = p2 + t2 * d2
  // Çapraz çarpım ile t1'i bul
  
  const cross = d1.x * d2.y - d1.y * d2.x;
  
  // Paralel çizgiler
  if (Math.abs(cross) < 0.0001) {
    return null;
  }
  
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  
  const t1 = (dx * d2.y - dy * d2.x) / cross;
  
  // Kesişim noktası
  return new THREE.Vector3(
    p1.x + t1 * d1.x,
    p1.y + t1 * d1.y,
    0
  );
}

/**
 * Önizleme çizgisinin yönü ile offset çizgisinin kesişim noktasını hesaplar
 * @param {THREE.Vector3} lastVertex - Son vertex (preview çizgisinin başlangıcı)
 * @param {THREE.Vector3} previewEnd - Preview çizgisinin hedefi (mouse konumu)
 * @param {Object} offsetLine - Offset çizgisi {start, end}
 * @returns {THREE.Vector3} - Kesişim noktası
 */
export function getPreviewRayIntersection(lastVertex, previewEnd, offsetLine) {
  // Preview çizgisinin yönü
  const previewDir = new THREE.Vector3().subVectors(previewEnd, lastVertex).normalize();
  
  // Offset çizgisinin yönü
  const offsetDir = new THREE.Vector3().subVectors(offsetLine.end, offsetLine.start).normalize();
  
  // Kesişim noktasını hesapla
  const intersection = getRayIntersection(lastVertex, previewDir, offsetLine.start, offsetDir);
  
  // Eğer kesişim bulunamazsa, perpendicular intersection kullan (fallback)
  if (!intersection) {
    return getPerpendicularIntersection(lastVertex, offsetLine);
  }
  
  return intersection;
}

/**
 * Belirli bir yönde (direction) ilerlerken offset çizgisi ile kesişim noktasını hesaplar
 * @param {THREE.Vector3} lastVertex - Son vertex (preview çizgisinin başlangıcı)
 * @param {THREE.Vector3} direction - Preview çizgisinin yönü (normalize edilmiş)
 * @param {Object} offsetLine - Offset çizgisi {start, end}
 * @returns {THREE.Vector3} - Kesişim noktası
 */
export function getDirectionalIntersection(lastVertex, direction, offsetLine) {
  // Offset çizgisinin yönü
  const offsetDir = new THREE.Vector3().subVectors(offsetLine.end, offsetLine.start).normalize();
  
  // Kesişim noktasını hesapla
  const intersection = getRayIntersection(lastVertex, direction, offsetLine.start, offsetDir);
  
  // Eğer kesişim bulunamazsa, perpendicular intersection kullan (fallback)
  if (!intersection) {
    return getPerpendicularIntersection(lastVertex, offsetLine);
  }
  
  return intersection;
}